import { isForbidden } from '@cwos/api-client';
import { t } from '@cwos/ui-shared';
import type { QueryLike } from './listState.js';

/**
 * `listState.ts`, for the things that are not lists.
 *
 * `listPhase` stopped an empty PANEL meaning three different things. It cannot
 * help a KPI tile, a badge or a headline number, because those are not lists
 * and the collapse they suffer is worse: a list that renders empty at least
 * LOOKS empty, whereas
 *
 *     <Stat value={String(q.data?.expired ?? 0)} />
 *
 * renders a confident `0` from a 500, from a 403, and from a request that has
 * not come back yet. Three non-answers set in the same typeface as the answer —
 * and the answer they imitate, "nothing is expired", is the reassuring one. The
 * reader cannot tell, and nothing on the screen invites them to doubt it.
 *
 * So: the same decision as `listPhase`, made once, for a scalar.
 */

/**
 * WHOSE population a figure counts.
 *
 * The second half of the same honesty problem, and it survives every fix to the
 * first. Suppose the number really did arrive, from a request that really did
 * succeed: `expired: 0` is still not "the organization has nothing expired" if
 * the server counted only the reader's own caseload. Both readings render as
 * the same glyph, and only one of them is a fact about the organization.
 *
 * `organization` is not the default. A caller has to say which population their
 * figure counts, for the same reason `fromFlag` makes `absentLabel` required:
 * choosing it is the moment somebody has to notice there was a choice.
 */
export type Population =
  /** Everyone. The figure is a fact about the organization. */
  | { kind: 'organization' }
  /**
   * A subset the reader's grants pick out. `who` completes "…of {who}" and
   * "Counts {who}", so it is a noun phrase: "your team", "you and the people
   * you supervise".
   */
  | { kind: 'narrowed'; who: string }
  /**
   * Which of the two above applies is not yet known — the permission set has
   * not loaded, or failed to. Rendering the figure as organization-wide here is
   * a guess, and it is the guess that is wrong in the dangerous direction.
   */
  | { kind: 'unknown' };

export const ORGANIZATION: Population = { kind: 'organization' };
export const UNKNOWN_POPULATION: Population = { kind: 'unknown' };

/** A subset, named. */
export function narrowedTo(who: string): Population {
  return { kind: 'narrowed', who };
}

/**
 * What a figure is, before anybody decides how to draw it.
 *
 * `value` is deliberately the ONLY member carrying a number. There is no
 * `value: number` on `failed` holding a zero "just in case", because the moment
 * one exists a call site will read it.
 */
export type FigurePhase =
  /** Not answered yet. Distinct from answered-with-nothing, which is the point. */
  | { kind: 'loading' }
  /** The server said 403. An answer, and never a zero. */
  | { kind: 'refused' }
  /** Asked and could not be told. Retryable, and never a zero. */
  | { kind: 'failed' }
  /**
   * The response arrived and this figure was not in it — suppressed, withheld,
   * or genuinely not computable. What matters here is only that it is not a
   * number; which of those it is belongs to the caller that knows.
   */
  | { kind: 'absent' }
  /** A real number, and the population it was counted over. */
  | { kind: 'value'; value: number; population: Population };

/**
 * The same ordering as `listPhase`, and for the same reasons.
 *
 * A 403 is decided first and regardless of cache: a permission taken away is an
 * answer about now. Then the absence of a payload, split by whether the request
 * failed or has simply not returned — react-query reports a disabled query as
 * neither loading nor errored, and "we have not asked" is a pending state, not
 * an answer of zero. Only then is the number looked at, and a `null` inside a
 * payload that DID arrive is `absent` rather than `failed`: the server
 * answered, it just did not answer with a number.
 */
export function figurePhase(
  query: QueryLike,
  value: number | null | undefined,
  population: Population,
): FigurePhase {
  if (query.isError && isForbidden(query.error)) return { kind: 'refused' };
  if (query.data === undefined) {
    if (query.isError) return { kind: 'failed' };
    return { kind: 'loading' };
  }
  if (value == null) return { kind: 'absent' };
  return { kind: 'value', value, population };
}

/**
 * What goes where the number goes.
 *
 * Never `'0'` for anything but a zero. Each placeholder is visibly not a number
 * — a reader scanning a row of tiles has to be stopped by them, not soothed.
 * `refused` uses the product-wide phrase from the withholding contract so a
 * person meets the same six words here as on a program lead.
 */
export const FIGURE_PLACEHOLDER: Record<Exclude<FigurePhase['kind'], 'value'>, string> = {
  loading: '…',
  refused: t('state.notShown'),
  failed: t('state.didntLoad'),
  absent: '—',
};

export function figureText(phase: FigurePhase, format: (value: number) => string): string {
  return phase.kind === 'value' ? format(phase.value) : FIGURE_PLACEHOLDER[phase.kind];
}

/**
 * The sentence under the number, or null when the number needs no defending.
 *
 * The `value` case is the one that matters: a narrowed figure gets a line
 * saying so and an organization-wide one gets nothing, so the ordinary screen
 * is unchanged and the sentence appears exactly where a reader could otherwise
 * get it wrong.
 */
export function figureNote(phase: FigurePhase): string | null {
  switch (phase.kind) {
    case 'loading':
      return null;
    case 'refused':
      return t('figure.refused');
    case 'failed':
      return t('figure.failed');
    case 'absent':
      return null;
    case 'value':
      return populationNote(phase.population);
  }
}

/** Whom the figure counts, or null when the answer is "everybody". */
export function populationNote(population: Population): string | null {
  switch (population.kind) {
    case 'organization':
      return null;
    case 'narrowed':
      return t('figure.counts', { who: population.who });
    case 'unknown':
      return t('figure.scopeUnknown');
  }
}

/**
 * A rate, and the population it is a rate OF.
 *
 * The derived-figure case, and it fails in its own specific way. "94%
 * compliant" computed over the eleven people a supervisor may see is a true
 * statement about eleven people and a false one about the organization — and
 * unlike a narrowed count, which at least looks small, a narrowed rate looks
 * exactly like the organization's rate. It is the same two digits.
 *
 * So a rate is never returned bare. `label` carries the population into the
 * words, which is the only place a reader can see it.
 */
export interface Rate {
  /** The percentage, 0–100, rounded. Null when there is nothing to divide by. */
  percent: number | null;
  /** "94% of your team" / "94% of the organization", or why there is no rate. */
  label: string;
  /** True when the denominator is a subset, so a caller can style it apart. */
  narrowed: boolean;
}

/**
 * A zero denominator is NOT a zero rate.
 *
 * `0 / 0` rendered as `0%` is the same lie in arithmetic clothing: "nobody is
 * compliant", said about a population with nobody in it. There is no rate, and
 * saying so is shorter than the wrong number.
 */
export function rateOver(
  numerator: number,
  denominator: number,
  population: Population,
  what: string,
): Rate {
  const of = populationPhrase(population);
  const narrowed = population.kind !== 'organization';
  if (denominator <= 0) {
    return { percent: null, label: t('rate.none', { what, of }), narrowed };
  }
  const percent = Math.round((numerator / denominator) * 100);
  return { percent, label: t('rate.of', { percent, of }), narrowed };
}

/** The population as it appears mid-sentence, after "of". */
export function populationPhrase(population: Population): string {
  switch (population.kind) {
    case 'organization':
      return t('rate.population.org');
    case 'narrowed':
      return population.who;
    case 'unknown':
      return t('rate.population.unknown');
  }
}

/**
 * A rate whose denominator is not a number cannot be computed, and the honest
 * answer is not a rate with a caveat — it is no rate at all.
 *
 * A denominator that was refused, failed or withheld makes every percentage
 * built on it a fabrication, and a percentage is the hardest figure on a screen
 * to disbelieve: it carries no units to look wrong by.
 */
export function rateFromPhases(
  numerator: FigurePhase,
  denominator: FigurePhase,
  what: string,
): Rate | null {
  if (numerator.kind !== 'value' || denominator.kind !== 'value') return null;
  return rateOver(numerator.value, denominator.value, denominator.population, what);
}
